import RAPIER from '@dimforge/rapier3d-compat'

import { quatFromAxisAngle } from '../engine/quat'
import type { ObjectBodyRegistry } from './objectBodyRegistry'
import {
  CARRY_CONTACT_SKIN,
  CARRY_IDENTITY_ROTATION,
  CONTROLLER_OFFSET,
  GRAVITY,
  GROUND_RAY_DIRECTION,
  VEHICLE_EXIT_ROOF_MARGIN,
  VEHICLE_GROUND_PROBE_EXTRA,
  VEHICLE_GROUND_SNAP_TOLERANCE,
  VEHICLE_PROMPT_DISTANCE,
  VEHICLE_SEAT_SINK,
  VEHICLE_SWEEP_GROUND_CLEAR,
  VEHICLE_SWEEP_SKIN,
} from './physicsConstants'
import type {
  AvatarDescriptor,
  PreviewWorld,
  Vec3,
  VehicleDescriptor,
} from '../types'
import {
  captureVehicleGeometry,
  computePartTransforms,
  DEFAULT_MAX_SPEED,
  DEFAULT_REVERSE_SPEED,
  exitWorldPosition,
  seatWorldPose,
  stepVehicleSpeed,
  stepVehicleYaw,
  vehicleForwardDir,
} from './vehicleMath'
import type { CapturedVehicleGeometry, VehicleDriveParams } from './vehicleMath'

/**
 * 乗降プロンプトの種別。'enter'=近くの車両に乗れる、'exit'=乗車中（降りられる）、
 * null=プロンプトなし。UI（乗降ボタン）の表示切替に使う。
 */
export type VehiclePromptKind = 'enter' | 'exit' | null

/**
 * 車両の乗車・運転・降車サブシステム。
 *
 * createAvatarController から「車両（VehicleSeat を持つモデル）の乗降・運転」を
 * 切り出したもの。乗車中の実行時状態（activeVehicle）・プロンプト対象
 * （nearbyVehicle）・UI 通知の既知値（lastNotifiedPrompt）はファクトリ内部に
 * 隠蔽し、本体には状態参照（isSeated / activePartIdSet）と操作
 * （triggerVehicleAction / abandonVehicle / clearPrompt / updateVehiclePrompt /
 * stepVehicleDrive）だけを公開する。歩行物理の可変状態には直接触れず、
 * 本体が注入する意図単位のコールバック（resetWalkStateForVehicleEntry /
 * snapRenderPositionTo / teleportAvatarTo）経由で操作する。
 */
export interface VehicleSystem {
  /** 乗車中か。本体は乗車中分岐・motion state（isSeated）に使う。 */
  isSeated(): boolean
  /**
   * 乗車中の車両の部品 id 集合（非乗車時は null）。
   * 乗車中の接触検知（detectSeatedTouches）で車両自身の部品を除外するために使う。
   */
  activePartIdSet(): ReadonlySet<string> | null
  /** 乗降アクション（E キー・乗降ボタン共用）。 */
  triggerVehicleAction(): void
  /**
   * 乗車をやめて車両を解放する（降車・死亡・テレポート・リセット共用。
   * アバターの移動はしない）。
   */
  abandonVehicle(): void
  abandonIfSeatMissing(): void
  /**
   * プロンプト対象を破棄し、UI のプロンプト表示を消す（死亡・リセット用。
   * 死亡演出中は stepPhysics がプロンプト更新へ到達しないため、本体が明示的に呼ぶ）。
   */
  clearPrompt(): void
  /** 乗降プロンプトの現況を計算し、変化していれば UI へ通知する。 */
  updateVehiclePrompt(): void
  /** 運転モードの 1 ステップ（部品一式とアバターを kinematic に動かす）。 */
  stepVehicleDrive(dt: number): void
}

/**
 * 車両サブシステムを生成する。
 *
 * @param deps.physicsWorld rapier の物理 World（接地レイ・車体スイープの実行元）。
 * @param deps.world 見た目・状態の真実源。部品姿勢の参照元かつ運転結果の反映先。
 * @param deps.avatarBody アバターの剛体（座席への kinematic 同期先・位置の参照元）。
 * @param deps.registry オブジェクト body レジストリ。部品の実在チェック（has）・
 *   collider 逆引き（objectIdOf）・降車時の broad phase 再登録（reregister）に使う。
 * @param deps.getAvatarCollider アバターのコライダー。本体所有の参照へ構築順に依存せずアクセスするため getter で受け取る。
 * @param deps.avatar アバター記述。size.y を座席・降車位置の高さ補正に使う。
 * @param deps.vehicleDescriptors シーン記述の車両一覧（descriptor.vehicles ?? []）。
 * @param deps.isDisposed コントローラが破棄済みか（本体のライフサイクル状態）。
 * @param deps.isPaused 一時停止中か（本体のライフサイクル状態）。
 * @param deps.isDead 死亡演出中か（本体の歩行状態）。
 * @param deps.isKeyHeld 物理・仮想いずれかでキーが押下中か（運転入力の参照）。
 * @param deps.isJumpHeld ジャンプキー（Space）押下中か（ハンドブレーキ入力）。
 * @param deps.resetWalkStateForVehicleEntry 乗車時に歩行系の一時状態を一括解除する
 *   （本体が所有する歩行可変状態への唯一の書き込み口）。
 * @param deps.snapRenderPositionTo 描画補間サンプルを指定位置へスナップする
 *   （乗車中は補間で座席から遅れないようにする）。
 * @param deps.teleportAvatarTo アバターを指定位置へ即時テレポートする（降車先への移動）。
 * @param deps.onVehiclePrompt 乗降プロンプトの表示状態が変わった瞬間に呼ばれる。
 * @param deps.onVehicleTouch 運転中に車体が CanTouch 部品へ新たに触れた瞬間に
 *   objectId で呼ばれる（実機の Car が触れたブロックを蹴るギミック用）。歩行の
 *   onTouch と同じ経路（emitTouch）へ流す。
 */
export const createVehicleSystem = (deps: {
  physicsWorld: RAPIER.World
  world: PreviewWorld
  avatarBody: RAPIER.RigidBody
  registry: Pick<ObjectBodyRegistry, 'objectIdOf' | 'has' | 'reregister'>
  getAvatarCollider: () => RAPIER.Collider
  avatar: AvatarDescriptor
  vehicleDescriptors: VehicleDescriptor[]
  isDisposed: () => boolean
  isPaused: () => boolean
  isDead: () => boolean
  isKeyHeld: (code: string) => boolean
  isJumpHeld: () => boolean
  resetWalkStateForVehicleEntry: () => void
  snapRenderPositionTo: (position: Vec3) => void
  teleportAvatarTo: (position: Vec3) => void
  onVehiclePrompt?: (prompt: VehiclePromptKind) => void
  onVehicleTouch?: (objectId: string) => void
}): VehicleSystem => {
  const {
    physicsWorld,
    world,
    avatarBody,
    registry,
    getAvatarCollider,
    avatar,
    vehicleDescriptors,
    isDisposed,
    isPaused,
    isDead,
    isKeyHeld,
    isJumpHeld,
    resetWalkStateForVehicleEntry,
    snapRenderPositionTo,
    teleportAvatarTo,
    onVehiclePrompt,
    onVehicleTouch,
  } = deps

  // 運転中に車体が触れている CanTouch 部品の集合。enter（新規接触）の瞬間だけ
  // onVehicleTouch を呼ぶための前フレーム基準（押し付け中の毎フレーム発火を防ぐ）。
  let prevVehicleTouched = new Set<string>()

  /**
   * シーン記述の車両のうち、座席・部品がシーンに実在するものだけを採用する
   * （ローダは形式チェックのみで、id の実在は物理側の責務）。
   */
  const vehicles: VehicleDescriptor[] = vehicleDescriptors
    .map((vehicle) => ({
      ...vehicle,
      partIds: vehicle.partIds.filter((id) => registry.has(id)),
    }))
    .filter(
      (vehicle) => registry.has(vehicle.seatId) && vehicle.partIds.length > 0,
    )

  /**
   * 乗車中の車両の実行時状態。シーン JSON にウェルド情報が無いため、乗車時に
   * 部品の相対配置をキャプチャし、partIds 一式を 1 つの剛体として kinematic に
   * 動かす（Fixed body のテレポート追従が物理へ反映する）。null は非乗車。
   */
  let activeVehicle: {
    readonly descriptor: VehicleDescriptor
    readonly geometry: CapturedVehicleGeometry
    readonly partIdSet: ReadonlySet<string>
    readonly params: VehicleDriveParams
    /** 水平移動スイープ用の車体ボックス（AABB から床ぎわを少し縮めた箱）。 */
    readonly sweepShape: RAPIER.Cuboid
    /** 乗車時に計測した車高（pivot から接地面までの距離）。接地スナップの基準。 */
    readonly groundClearance: number
    motion: { position: Vec3; yawDelta: number; speed: number }
    verticalVelocity: number
  } | null = null
  /** プロンプト対象（最寄りの座席が範囲内にある車両）。毎ステップ更新する。 */
  let nearbyVehicle: VehicleDescriptor | null = null
  /** 直近に UI へ通知したプロンプト種別（変化したときだけ通知する）。 */
  let lastNotifiedPrompt: VehiclePromptKind = null

  /** 車両部品（と自分自身）をクエリ対象から除くフィルタ。 */
  const excludeVehicleParts =
    (partIdSet: ReadonlySet<string>) =>
    (collider: RAPIER.Collider): boolean => {
      const objectId = registry.objectIdOf(collider.handle)
      return objectId === undefined || !partIdSet.has(objectId)
    }

  /** プロンプト種別を UI へ通知する（変化したときだけ）。 */
  const notifyVehiclePrompt = (prompt: VehiclePromptKind): void => {
    if (prompt !== lastNotifiedPrompt) {
      lastNotifiedPrompt = prompt
      onVehiclePrompt?.(prompt)
    }
  }

  /**
   * 乗降プロンプトの現況を計算し、変化していれば UI へ通知する。
   * 非乗車時は最寄り座席との 3D 距離が VEHICLE_PROMPT_DISTANCE 以内の車両を
   * プロンプト対象として記録する（E キー・乗降ボタンの乗車対象）。
   */
  const updateVehiclePrompt = (): void => {
    if (!activeVehicle) {
      nearbyVehicle = null
      if (vehicles.length > 0 && !isDead()) {
        const avatarPos = avatarBody.translation()
        const states = world.getState()
        let bestDistance = VEHICLE_PROMPT_DISTANCE
        for (const vehicle of vehicles) {
          const seat = states.get(vehicle.seatId)
          if (!seat) {
            continue
          }
          const distance = Math.hypot(
            seat.position.x - avatarPos.x,
            seat.position.y - avatarPos.y,
            seat.position.z - avatarPos.z,
          )
          if (distance <= bestDistance) {
            bestDistance = distance
            nearbyVehicle = vehicle
          }
        }
      }
    }
    notifyVehiclePrompt(
      activeVehicle ? 'exit'
      : nearbyVehicle ? 'enter'
      : null,
    )
  }

  /**
   * 乗車をやめて車両を解放する（降車・死亡・テレポート・リセット共用。
   * アバターの移動はしない）。運転で動かした部品の collider は broad phase が
   * 古いままで KCC・レイから見えないため、全部品を再登録して直す。
   */
  const abandonVehicle = (): void => {
    const vehicle = activeVehicle
    if (!vehicle) {
      return
    }
    activeVehicle = null
    prevVehicleTouched = new Set<string>()
    for (const id of vehicle.descriptor.partIds) {
      registry.reregister(id)
    }
  }

  /**
   * プロンプト対象を破棄し、UI のプロンプト表示を消す（死亡・リセット用）。
   */
  const clearPrompt = (): void => {
    nearbyVehicle = null
    notifyVehiclePrompt(null)
  }

  const abandonIfSeatMissing = (): void => {
    if (activeVehicle && !registry.has(activeVehicle.descriptor.seatId)) {
      abandonVehicle()
      clearPrompt()
    }
  }

  /** アバターを座席上へ配置する（位置・向き・描画サンプルの同期）。 */
  const applySeatedAvatarPose = (): void => {
    const vehicle = activeVehicle
    if (!vehicle) {
      return
    }
    const seat = seatWorldPose(vehicle.geometry, vehicle.motion)
    const center = {
      x: seat.position.x,
      y: seat.position.y + avatar.size.y / 2 - VEHICLE_SEAT_SINK,
      z: seat.position.z,
    }
    avatarBody.setTranslation(center, true)
    world.setAvatarPose(center, seat.yaw)
    // 乗車中は車体と同じ「物理確定位置」で描く（補間で座席から遅れない）。
    snapRenderPositionTo(center)
  }

  /**
   * 車両へ乗車する（実機の ProximityPrompt → Seat:Sit 相当）。
   * 現在の World 状態から部品の相対配置・車高をキャプチャし、運転モードへ入る。
   */
  const enterVehicle = (vehicle: VehicleDescriptor): void => {
    const states = world.getState()
    const geometry = captureVehicleGeometry(
      (id) => {
        const state = states.get(id)
        return state ?
            {
              position: state.position,
              rotationY: state.rotationY,
              orientation: state.orientation,
              size: state.size,
            }
          : undefined
      },
      vehicle.partIds,
      vehicle.seatId,
    )
    if (!geometry) {
      return
    }
    const partIdSet = new Set(vehicle.partIds)
    // 車高（pivot→接地面）を計測する。地面が見つからない場合は AABB 半径で代用
    // （空中の車。最初の接地で正しい車高に吸着する）。
    const groundHit = physicsWorld.castRay(
      new RAPIER.Ray(geometry.pivot, GROUND_RAY_DIRECTION),
      geometry.halfExtents.y + VEHICLE_GROUND_PROBE_EXTRA,
      true,
      RAPIER.QueryFilterFlags.EXCLUDE_SENSORS,
      undefined,
      getAvatarCollider(),
      undefined,
      excludeVehicleParts(partIdSet),
    )
    activeVehicle = {
      descriptor: vehicle,
      geometry,
      partIdSet,
      params: {
        maxSpeed: vehicle.maxSpeed ?? DEFAULT_MAX_SPEED,
        reverseSpeed: vehicle.reverseSpeed ?? DEFAULT_REVERSE_SPEED,
      },
      sweepShape: new RAPIER.Cuboid(
        Math.max(0.05, geometry.halfExtents.x - VEHICLE_SWEEP_SKIN),
        Math.max(0.05, geometry.halfExtents.y - VEHICLE_SWEEP_GROUND_CLEAR),
        Math.max(0.05, geometry.halfExtents.z - VEHICLE_SWEEP_SKIN),
      ),
      groundClearance:
        groundHit ? groundHit.timeOfImpact : geometry.halfExtents.y,
      motion: { position: { ...geometry.pivot }, yawDelta: 0, speed: 0 },
      verticalVelocity: 0,
    }
    prevVehicleTouched = new Set<string>()
    // 歩行系の一時状態を解除する（座席へ瞬間移動するため誤検知を防ぐ）。
    // テレポート扱い（respawnGeneration 加算）も含み、レンダラの誤着地屈伸・
    // 距離スパイクを抑止する（実体は本体の resetWalkStateForVehicleEntry）。
    resetWalkStateForVehicleEntry()
    applySeatedAvatarPose()
    updateVehiclePrompt()
  }

  /**
   * 降車する（実機の ExitSeat → ExitPosition への移動相当）。降車位置が壁等で
   * 塞がっている場合は車の屋根上へ退避する（実機の「シート上へ MoveTo」の簡略版）。
   */
  const exitVehicle = (): void => {
    const vehicle = activeVehicle
    if (!vehicle) {
      return
    }
    const seat = seatWorldPose(vehicle.geometry, vehicle.motion)
    const exit = exitWorldPosition(
      vehicle.geometry,
      vehicle.motion,
      vehicle.descriptor.exitOffset,
    )
    // 座席→降車位置（ドア側）の遮蔽チェックは座席の高さで水平に掃引する。
    // 降車先の地面へ向けて斜めに掃くと着地面そのものに必ず接触し、常に
    // 「塞がっている」誤判定になるため、水平で戸口の障害物だけを見る。
    const seatCenter = { ...seat.position }
    const blocked = physicsWorld.castShape(
      seatCenter,
      CARRY_IDENTITY_ROTATION,
      { x: exit.x - seatCenter.x, y: 0, z: exit.z - seatCenter.z },
      getAvatarCollider().shape,
      CARRY_CONTACT_SKIN,
      1,
      false,
      RAPIER.QueryFilterFlags.EXCLUDE_SENSORS,
      undefined,
      getAvatarCollider(),
      undefined,
      excludeVehicleParts(vehicle.partIdSet),
    )
    // 実機は HumanoidRootPart = ExitPosition（腰位置）だが、本プレビューの
    // アバター位置はカプセル中心（全身の中点）基準のため、そのまま使うと
    // 足が地面へ埋まる。ExitPosition の XZ で接地面を探し、その上へ立たせる
    // （地面が無ければ ExitPosition を足元とみなし、落下は重力に任せる）。
    const exitGroundHit = physicsWorld.castRay(
      new RAPIER.Ray(
        { x: exit.x, y: seatCenter.y, z: exit.z },
        GROUND_RAY_DIRECTION,
      ),
      vehicle.geometry.halfExtents.y + VEHICLE_GROUND_PROBE_EXTRA,
      true,
      RAPIER.QueryFilterFlags.EXCLUDE_SENSORS,
      undefined,
      getAvatarCollider(),
      undefined,
      excludeVehicleParts(vehicle.partIdSet),
    )
    const exitCenterY =
      exitGroundHit ?
        seatCenter.y -
        exitGroundHit.timeOfImpact +
        avatar.size.y / 2 +
        CONTROLLER_OFFSET
      : exit.y + avatar.size.y / 2
    const finalTarget =
      blocked ?
        {
          x: vehicle.motion.position.x,
          y:
            vehicle.motion.position.y +
            vehicle.geometry.halfExtents.y +
            avatar.size.y / 2 +
            VEHICLE_EXIT_ROOF_MARGIN,
          z: vehicle.motion.position.z,
        }
      : { x: exit.x, y: exitCenterY, z: exit.z }
    abandonVehicle()
    teleportAvatarTo(finalTarget)
    updateVehiclePrompt()
  }

  /** 乗降アクション（E キー・乗降ボタン共用）。 */
  const triggerVehicleAction = (): void => {
    if (isDisposed() || isPaused() || isDead()) {
      return
    }
    if (activeVehicle) {
      exitVehicle()
      return
    }
    if (nearbyVehicle) {
      enterVehicle(nearbyVehicle)
    }
  }

  /**
   * 運転モードの 1 ステップ。キー入力を実機の運転席（Keymap）と同じ意味論で
   * 解釈し（W/↑=前進・S/↓=ブレーキ/後退・A/D=操舵・Space=ハンドブレーキ）、
   * 部品一式とアバターを kinematic に動かす。
   */
  const stepVehicleDrive = (dt: number): void => {
    const vehicle = activeVehicle
    if (!vehicle) {
      return
    }
    const has = isKeyHeld
    const throttle =
      (has('KeyW') || has('ArrowUp') ? 1 : 0) -
      (has('KeyS') || has('ArrowDown') ? 1 : 0)
    const steer =
      (has('KeyA') || has('ArrowLeft') ? 1 : 0) -
      (has('KeyD') || has('ArrowRight') ? 1 : 0)
    const input = {
      throttle,
      steer,
      handbrake: isJumpHeld(),
    }
    let speed = stepVehicleSpeed(
      vehicle.motion.speed,
      input,
      dt,
      vehicle.params,
    )
    const yawInc = stepVehicleYaw(speed, steer, dt, vehicle.params)
    const yawDelta = vehicle.motion.yawDelta + yawInc
    const forward = vehicleForwardDir(vehicle.geometry, {
      ...vehicle.motion,
      yawDelta,
    })
    let deltaX = forward.x * speed * dt
    let deltaZ = forward.z * speed * dt
    // このフレームに車体が触れた CanTouch 部品（enter 判定で onVehicleTouch を呼ぶ）。
    const vehicleTouched = new Set<string>()
    if (deltaX !== 0 || deltaZ !== 0) {
      // 車体ボックスを移動方向へ掃引し、壁・他の車などの手前で停止する
      // （kinematic 移動は衝突解決されないため、トンネリングをここで防ぐ）。
      const hit = physicsWorld.castShape(
        vehicle.motion.position,
        quatFromAxisAngle('y', yawDelta),
        { x: deltaX, y: 0, z: deltaZ },
        vehicle.sweepShape,
        VEHICLE_SWEEP_SKIN,
        1,
        false,
        RAPIER.QueryFilterFlags.EXCLUDE_SENSORS,
        undefined,
        getAvatarCollider(),
        undefined,
        excludeVehicleParts(vehicle.partIdSet),
      )
      if (hit) {
        const scale = Math.max(0, hit.time_of_impact)
        deltaX *= scale
        deltaZ *= scale
        // 衝突したら停止する（壁に押し付け続けない。後退で離れられる）。
        speed = 0
        // 触れた相手が CanTouch 部品なら Touched を発火させる（実機の Car が触れた
        // ④ブロックを蹴るギミック）。蹴られたブロックが動けば次フレーム以降は前進できる。
        const touchedId = registry.objectIdOf(hit.collider.handle)
        if (
          touchedId !== undefined &&
          !vehicle.partIdSet.has(touchedId) &&
          world.getState().get(touchedId)?.canTouch !== false
        ) {
          vehicleTouched.add(touchedId)
        }
      }
    }
    // enter（前フレームに触れていなかった部品）だけ発火する。押し付け中の毎フレーム
    // 連打を防ぎ、歩行時の Touched（enter のみ）と挙動を揃える。
    for (const id of vehicleTouched) {
      if (!prevVehicleTouched.has(id)) {
        onVehicleTouch?.(id)
      }
    }
    prevVehicleTouched = vehicleTouched
    const nextX = vehicle.motion.position.x + deltaX
    const nextZ = vehicle.motion.position.z + deltaZ
    let nextY = vehicle.motion.position.y
    // 接地スナップ: 乗車時の車高を保って地面に吸着する。地面が許容距離より
    // 離れたら（段差・崖から飛び出した）重力落下に切り替える。
    const groundHit = physicsWorld.castRay(
      new RAPIER.Ray({ x: nextX, y: nextY, z: nextZ }, GROUND_RAY_DIRECTION),
      vehicle.groundClearance + VEHICLE_GROUND_SNAP_TOLERANCE,
      true,
      RAPIER.QueryFilterFlags.EXCLUDE_SENSORS,
      undefined,
      getAvatarCollider(),
      undefined,
      excludeVehicleParts(vehicle.partIdSet),
    )
    if (groundHit) {
      nextY = nextY - groundHit.timeOfImpact + vehicle.groundClearance
      vehicle.verticalVelocity = 0
    } else {
      vehicle.verticalVelocity -= GRAVITY * dt
      nextY += vehicle.verticalVelocity * dt
    }
    const movedY = nextY !== vehicle.motion.position.y
    vehicle.motion = {
      position: { x: nextX, y: nextY, z: nextZ },
      yawDelta,
      speed,
    }
    // 停車中（移動も旋回もない）は World へ書かない。同値の状態差し替えでも
    // 参照が変わって全購読者の再同期が走るため、無駄な毎ステップ通知を避ける。
    if (deltaX !== 0 || deltaZ !== 0 || yawInc !== 0 || movedY) {
      world.setDynamicTransforms(
        computePartTransforms(vehicle.geometry, vehicle.motion).map(
          (transform) => ({
            objectId: transform.objectId,
            position: transform.position,
            orientation: transform.orientation,
          }),
        ),
      )
      applySeatedAvatarPose()
    }
  }

  return {
    isSeated: () => activeVehicle !== null,
    activePartIdSet: () => (activeVehicle ? activeVehicle.partIdSet : null),
    triggerVehicleAction,
    abandonVehicle,
    abandonIfSeatMissing,
    clearPrompt,
    updateVehiclePrompt,
    stepVehicleDrive,
  }
}
